import { useChain } from '../context/ChainContext';
import { ChevronDown, Network, Check } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// Tool pages treat Krown as a normal scope filter (they render Krown-specific
// data inline). Everywhere else, picking Krown opens its dedicated dashboard.
const KROWN_SCOPED_PATHS = ['/contracts', '/labels', '/analytics', '/archive', '/firstbuys'];

export function ChainSelector() {
  const { chain, setChain, chainInfo, chains } = useChain();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    function onClick(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Krown is served by its own internal-indexer dashboard (not the DexScreener
  // flow). From the Explorer / simulators, picking it opens that dashboard; on
  // tool pages it just scopes the current view to Krown.
  const pick = (id) => {
    setChain(id);
    setOpen(false);
    const isKrownScopedPage = KROWN_SCOPED_PATHS.some((p) => location.pathname.startsWith(p));
    if (id === 'krown' && !isKrownScopedPage) navigate('/dashboard');
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        data-testid="chain-selector-btn"
        className="h-8 inline-flex items-center gap-1.5 px-2.5 rounded-md text-xs font-medium border border-border bg-card hover:bg-muted transition-colors"
        aria-label="Select chain"
      >
        <Network className="w-3.5 h-3.5 text-blue-400" />
        <span className="hidden sm:inline ml-0.5">{chainInfo.shortLabel}</span>
        <span className="sm:hidden">{chainInfo.icon}</span>
        <ChevronDown className={`w-3 h-3 ml-0.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-1 w-48 max-h-80 overflow-y-auto rounded-lg border border-border bg-popover shadow-xl z-50 py-1 text-sm"
          data-testid="chain-selector-menu"
        >
          {chains.map(c => (
            <button
              key={c.id}
              type="button"
              onClick={() => pick(c.id)}
              data-testid={`chain-option-${c.id}`}
              className={`w-full flex items-center justify-between px-3 py-2 text-left hover:bg-muted transition-colors ${chain === c.id ? 'text-amber-400' : 'text-foreground'}`}
            >
              <span className="flex items-center gap-2">
                <span
                  className="w-11 h-5 rounded-full flex items-center justify-center text-[9px] font-bold tracking-tight shrink-0"
                  style={c.accent
                    ? { backgroundColor: `${c.accent}22`, color: c.accent }
                    : undefined}
                >
                  {c.shortLabel}
                </span>
                <span className="text-xs">{c.label}</span>
              </span>
              {chain === c.id && <Check className="w-3.5 h-3.5" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
